import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/cn';

export interface LogoMarkProps {
  size?: number;
  className?: string;
  /** Accessible name; omit when a wordmark is next to it. */
  title?: string;
}

/**
 * Tibyan mark (brand identity §5.1–5.2): eight-point khatam star (union of two squares, one rotated
 * 45°, inscribed in r = 90) with an open book whose gutter becomes a rising ray — both cut out of the
 * star as negative space.
 * Star in currentColor (primary on light, parchment on dark), page edges in gold. Never mirrored.
 */
export function LogoMark({ size = 32, className, title }: LogoMarkProps) {
  const uid = useId().replace(/:/g, '');
  const maskId = `tb-cut-${uid}`;
  const simplified = size < 24;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={cn('logo-mark shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      <defs>
        <mask id={maskId}>
          <rect width="200" height="200" fill="white" />
          {simplified ? (
            <>
              <path d="M94 128 L94 52 L106 52 L106 128 Z" fill="black" />
              <path d="M46 118 L94 134 L94 146 L46 130 Z M154 118 L106 134 L106 146 L154 130 Z" fill="black" />
            </>
          ) : (
            <>
              <path d="M93 124 L100 44 L107 124 Z" fill="black" />
              <path d="M100 124 C 82 118, 60 120, 44 112 L44 132 C 62 138, 84 136, 100 144 Z" fill="black" />
              <path d="M100 124 C 118 118, 140 120, 156 112 L156 132 C 138 138, 116 136, 100 144 Z" fill="black" />
            </>
          )}
        </mask>
      </defs>
      <path
        d="M100 10 L126.4 36.4 L163.6 36.4 L163.6 73.6 L190 100 L163.6 126.4 L163.6 163.6 L126.4 163.6 L100 190 L73.6 163.6 L36.4 163.6 L36.4 126.4 L10 100 L36.4 73.6 L36.4 36.4 L73.6 36.4 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinejoin="round"
        mask={`url(#${maskId})`}
      />
      {simplified ? null : (
        <g fill="none" strokeWidth="2" strokeLinecap="round" style={{ stroke: 'var(--logo-edge)' }}>
          <path d="M46 114 C 62 121, 84 119, 100 126" />
          <path d="M154 114 C 138 121, 116 119, 100 126" />
        </g>
      )}
    </svg>
  );
}

export interface LogoProps {
  size?: number;
  className?: string;
  showWordmark?: boolean;
}

/**
 * Horizontal lockup: mark on the start side, stacked bilingual wordmark (brand identity §5.3).
 * The name in the interface language leads: in Arabic «تبيان» (Amiri 700) is the large line and "Tibyan" the small
 * one beneath it; in English/French the order and sizes swap. Both lines align to the side next to the mark, and the
 * Arabic line keeps a real line-height because Amiri's tall letters are clipped at leading-none.
 */
export function Logo({ size = 36, className, showWordmark = true }: LogoProps) {
  const { i18n } = useTranslation();
  const arabicFirst = (i18n.resolvedLanguage ?? i18n.language ?? 'ar').startsWith('ar');
  const arabic = (
    <span
      key="ar"
      lang="ar"
      dir="rtl"
      className={cn('block text-heading', arabicFirst ? 'text-xl font-bold' : 'text-md')}
      style={{ fontFamily: 'var(--font-display-ar)', lineHeight: 1.35 }}
    >
      تبيان
    </span>
  );
  const latin = (
    <span
      key="en"
      lang="en"
      dir="ltr"
      className={cn('block text-heading', arabicFirst ? 'text-xs font-medium uppercase text-secondary' : 'text-lg font-semibold')}
      style={{
        fontFamily: 'var(--font-display-latin)',
        letterSpacing: arabicFirst ? '0.12em' : 'var(--tracking-tight)',
        lineHeight: 1.15,
      }}
    >
      Tibyan
    </span>
  );
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <LogoMark size={size} title={showWordmark ? undefined : 'Tibyan'} />
      {showWordmark ? (
        <span className="flex flex-col items-start">{arabicFirst ? [arabic, latin] : [latin, arabic]}</span>
      ) : null}
    </span>
  );
}
